<template>
  <FormContainer>
    <template #title></template>
    <template #content>
      <div class="form__bloc">
        <BasicCard
          class="col-9 col-md-18 col-sm-36"
          title="Numéro Dossier"
          :subtitles="[{ text: model?.codeDossier ?? '' }]"
        >
          <template #action>
            <BasicTooltip
              v-focusable="{
                onEnter: () => displayDossier,
              }"
              label="Consulter le dossier"
            >
              <BasicIcon
                name="eye"
                color="primary-600"
                pointer
                @click="displayDossier"
              />
            </BasicTooltip>
          </template>
        </BasicCard>
        <BasicCard
          class="col-9 col-md-18 col-sm-36"
          title="Ref. Interne Dossier"
          :subtitles="[{ text: model?.referenceInterneDossier ?? '' }]"
        />
        <BasicCard
          v-if="model?.sousDispositif === 'CIPI' || model?.sousDispositif === 'CDPI'"
          class="col-9 col-md-18 col-sm-36"
          title="Numéro de DRF"
          :subtitles="[{ text: model?.dossier?.numeroDRF ? model?.dossier?.numeroDRF : '-' }]"
        />
        <BasicCard
          v-if="typePublicLibelle"
          class="col-9 col-md-18 col-sm-36"
          title="Type de public"
          :subtitles="[{ text: typePublicLibelle }]"
        />
      </div>
      <div class="form__bloc">
        <BasicCard
          class="col-9 col-md-18 col-sm-36"
          title="Intitulé de la formation"
          :subtitles="[{ text: model?.dossier?.intitule ?? '' }]"
        />
        <BasicCard
          class="col-9 col-md-18 col-sm-36"
          title="Date de début"
          :subtitles="[{ text: formatDate(model?.dossier?.dateDebut) ?? '' }]"
        />
        <BasicCard
          class="col-9 col-md-18 col-sm-36"
          title="Date de fin"
          :subtitles="[{ text: formatDate(model?.dossier?.dateFin) ?? '' }]"
        />
        <BasicCard
          class="col-9 col-md-18 col-sm-36"
          title="Durée de formation en heures"
          :subtitles="[{ text: dureeHorsPositionnement.toString() ?? '' }]"
        />
      </div>
      <div class="form__bloc">
        <BasicCard
          class="col-9 col-md-18 col-sm-36"
          title="Prestataire(s)"
          :subtitles="[{ text: prestatairesList }]"
        />
        <BasicCard
          class="col-9 col-md-18 col-sm-36"
          title="Stagiaire(s)"
          :subtitles="[{ text: stagiairesList }]"
        />
      </div>
      <!-- SI DRF à gérer -->
      <!-- <div class="form__bloc">
        <BasicCard
          class="col-9 col-md-18 col-sm-36"
          title="DRF :"
          :subtitles="[{ text: model?.etablissementAdherentPrincipal?.denomination ?? '' }]"
        />
      </div> -->
      <!-- Specs ? -->
      <div class="form__bloc">
        <WrapperCheckbox
          v-model="validationNotice"
          :alertLabel="meta.validationNotice?.$messageErreurDirty!"
          :readonly
          :required="meta.validationNotice?.$status === PropertyStatus.Required"
          class="col-36"
        >
          <BasicText>
            J'ai pris connaissance de la
            <BasicLink
              label="notice"
              @link-click="
                redirectExterne('https://www.akto.fr/content/uploads/2023/05/NOTICE-POUR-LES-ACTIONS-DE-FORMATION.pdf')
              "
            />
            comprenant les informations liées au traitement des données à caractère personnel.
          </BasicText>
        </WrapperCheckbox>
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { PropertyStatus } from '@/features/domaine/property-status'
import { TypePublic, TypePublicLibelle } from '@/features/domaine/types/opco/shared'
import { DEMANDE_STORE } from '@/features/interface/demande/shared/components'
import FormContainer from '@/features/interface/demande/shared/components/FormContainer.vue'
import { dateFormatter, isDate, redirectExterne } from '@/features/shared/tools'
import { navigateTo } from '@/router/navigateTo'
import { storeToRefs } from 'pinia'
import { computed, inject } from 'vue'

  const store = inject(DEMANDE_STORE)!.getStore<'MODEL_DR'>()
  const { model, meta, readonly } = storeToRefs(store)
  const { setupFieldWithKey } = store

  const validationNotice = setupFieldWithKey<boolean>('validationNotice')
  const displayDossier = () => {
    const domaine = model.value?.sousDispositif === 'FpeOutils' ? 'tt' : 'formations'
    return navigateTo({ to: 'detail', domaine, module: 'dossier', code: model.value!.codeDossier! }, true)
  }

  const typePublicLibelle = computed(() => {
    const typePublic = model.value?.typePublic
    if (typePublic == null) return null
    const key = typeof typePublic === 'number' ? TypePublic[typePublic] : typePublic
    return key ? TypePublicLibelle[key] : null
  })

  const formatDate = (value: any) => (isDate(value) ? dateFormatter(value) : '')

  const stagiairesList = computed(() => {
    const stagiaires = model.value?.stagiaires
    if (!stagiaires || stagiaires.length === 0) return ''
    const first = stagiaires[0]?.prenom + ' ' + stagiaires[0]?.nom
    const others = stagiaires.length - 1
    return first + (others > 0 ? ` + ${others}` : '')
  })

  const prestatairesList = computed(() => {
    const modules = model.value?.modules
    if (!modules || modules.length === 0) return ''

    return modules
      .map((module) => {
        return module.etablissementPrestataire.denomination
      })
      .filter((item) => item !== '')
      .join(', ')
  })

  const dureeHorsPositionnement = computed(() => {
    const modules = model.value?.modules
    if (!modules || modules.length === 0) return ''

    const nbHeureModulesHorsPositionnement = modules
      ?.filter((x) => x.typeModule !== 'BilanPositionnement')
      .reduce((sum, module) => sum + (module.duree ?? 0), 0)

    return nbHeureModulesHorsPositionnement
  })

  // const consentementTraitementDonneesModel = setupFieldWithKey<boolean>('consentementTraitementDonnees')
</script>
<style scoped lang="less"></style>
